import { EditableInfoPage, SubmitProps } from '@/components/editable-info-page';
import { Card } from '@/components/ui/card';
import { useToast } from '@/components/ui/hooks/use-toast';
import {
  getAnnouncements,
  getCoachAvailabilities,
  getMeCoach,
  updateCoachAvailabilities,
  updateMeCoach,
} from '@/lib/api-calls';
import {
  coachAvailabilityFormSchema,
  CoachAvailabilityFormState,
  groupAvailabilitiesByDay,
} from '@/lib/coach-availability-utils';
import { airServerErrorFrom } from '@/lib/error-handling';
import { FrequentTimeZones } from '@airreading/common';
import { SortedDaysOfWeek, TeachingPreference, UpdateCoachAvailabilitiesOptions } from '@airreading/common/data';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Temporal } from 'temporal-polyfill';
import { WeeklyHoursFormRow } from './weekly-hours-form-row';
import TimezoneIcon from '../assets/timezone-icon.svg';
import ScheduleMidnightImage from '../assets/schedule-midnight.png';
import { TooltipPill } from '@/components/ui/tooltip-pill';
import { findTimeZoneByValue } from '@/lib/time-zones';
import { getTeachingPreferenceLabel } from '@/lib/coach-utils';
import { useFeatureFlag } from '@/hooks/use-feature-flags';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

export function WeeklyHoursList() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [formKey, setFormKey] = useState(0);
  const { data: profile } = useSuspenseQuery(getMeCoach());
  const { data: coachAvailabilities } = useSuspenseQuery(getCoachAvailabilities());
  const displayAndSelectionTimeZone = profile.timeZone || Temporal.Now.timeZoneId() || FrequentTimeZones.UsCentral;
  const { standardAbbr } = findTimeZoneByValue(displayAndSelectionTimeZone) || {};
  const teachingPreferenceEnabled = useFeatureFlag('teaching-preference', false);

  const [pastMidnightSlot, setPastMidnightSlot] = useState<{ dayId: number; index: number } | null>(null);

  const { mutateAsync } = useMutation({
    mutationFn: updateCoachAvailabilities,
  });
  findTimeZoneByValue;
  const onSubmit = async ({ formData, disableEditing }: SubmitProps<CoachAvailabilityFormState>) => {
    try {
      const data: UpdateCoachAvailabilitiesOptions = formData.availabilitiesByDay.flatMap(({ day, availabilities }) => {
        return availabilities.map((time) => ({
          day,
          startPlainTime: time.startPlainTime,
          endPlainTime: time.endPlainTime,
          timeZone: displayAndSelectionTimeZone,
        }));
      });

      // Update teaching preferences
      const currentPreferences = [...profile.teachingPreferences].sort();
      const sortedNewPreferences = [...formData.teachingPreferences].sort();
      const preferencesChanged =
        currentPreferences.length !== sortedNewPreferences.length ||
        currentPreferences.some((pref, idx) => pref !== sortedNewPreferences[idx]);

      if (preferencesChanged) {
        await updateMeCoach({
          ...profile,
          timeZone: displayAndSelectionTimeZone,
          currentTimeZone: displayAndSelectionTimeZone,
          firstName: profile.firstName ?? '',
          lastName: profile.lastName ?? '',
          displayName: profile.displayName ?? '',
          secondaryEmail: profile.secondaryEmail ?? '',
          phone: profile.phone ?? '',
          teachingPreferences: formData.teachingPreferences,
        });
        await queryClient.invalidateQueries({ queryKey: getMeCoach().queryKey });
      }

      await mutateAsync(data);
      await queryClient.invalidateQueries(getCoachAvailabilities());
      await queryClient.invalidateQueries(getAnnouncements());

      toast({
        description: 'Teacher availability updated successfully',
      });

      setFormKey((k) => k + 1);
      disableEditing();
    } catch (e: unknown) {
      toast({
        description:
          airServerErrorFrom(e)?.userFacingMessage ??
          (e instanceof Error
            ? e.message
            : 'Unable to update teacher availability. Please try again or contact Air Reading support.'),
      });
    }
  };

  const defaultValues: CoachAvailabilityFormState = {
    ...groupAvailabilitiesByDay(coachAvailabilities, displayAndSelectionTimeZone),
    teachingPreferences: profile.teachingPreferences,
  };

  const handlePastMidnightClick = (dayId: number, index: number) => {
    const slot = { dayId, index };
    setPastMidnightSlot(slot);
  };

  useEffect(() => {
    if (pastMidnightSlot) return;
    const timer = setTimeout(() => {
      document.body.style.pointerEvents = '';
    }, 100);
    return () => clearTimeout(timer);
  }, [pastMidnightSlot]);

  return (
    <>
      <Card className="mb-5 shadow-md p-2">
        <EditableInfoPage
          key={formKey}
          headerText={
            <>
              Weekly hours
              <div className="font-normal text-base leading-6 tracking-tight align-middle ml-2">
                (Enter your teaching availability. All times will be saved in your local time zone.)
              </div>
            </>
          }
          formSchema={coachAvailabilityFormSchema}
          defaultValues={defaultValues}
          onSubmit={onSubmit}
          showRedDot={false}
          data-testid="my-schedule-weekly-hours-header"
        >
          {({ form, editing }) => {
            const teachingPreferenceMode = (() => {
              const prefs = form.watch('teachingPreferences') ?? [];
              const hasRegular = prefs.includes(TeachingPreference.regular_schedule);
              const hasSub = prefs.includes(TeachingPreference.substitute_teaching);

              if (hasRegular && hasSub) return 'both';
              if (hasRegular) return 'regular';
              if (hasSub) return 'substitute';
              return '';
            })();

            return (
              <>
                <div>
                  <TooltipPill labelTooltip="To change your local time zone, please visit your Account.">
                    <div className="flex items-center gap-1" data-testid="my-schedule-weekly-hours-timezone">
                      <img src={TimezoneIcon} alt="Time Zone" />
                      <span className="text-base text-[#404040] font-medium">
                        {displayAndSelectionTimeZone} {standardAbbr && `(${standardAbbr})`}
                      </span>
                    </div>
                  </TooltipPill>
                </div>
                {teachingPreferenceEnabled && (
                  <>
                    {editing ? (
                      <div className="mb-2 pb-6" data-testid="my-schedule-weekly-hours-teaching-preference">
                        <RadioGroup
                          className="flex gap-6 items-center pt-4"
                          value={teachingPreferenceMode}
                          onValueChange={(value) => {
                            switch (value) {
                              case 'regular':
                                form.setValue('teachingPreferences', [TeachingPreference.regular_schedule], {
                                  shouldDirty: true,
                                  shouldTouch: true,
                                });
                                break;
                              case 'substitute':
                                form.setValue('teachingPreferences', [TeachingPreference.substitute_teaching], {
                                  shouldDirty: true,
                                  shouldTouch: true,
                                });
                                break;
                              case 'both':
                                form.setValue(
                                  'teachingPreferences',
                                  [TeachingPreference.regular_schedule, TeachingPreference.substitute_teaching],
                                  { shouldDirty: true, shouldTouch: true },
                                );
                                break;
                              default:
                                form.setValue('teachingPreferences', [], { shouldDirty: true, shouldTouch: true });
                            }
                          }}
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem
                              value="regular"
                              id="teaching-pref-regular"
                              data-testid="my-schedule-weekly-hours-radio-regular"
                            />
                            <label htmlFor="teaching-pref-regular" className="text-sm">
                              Regular schedule only
                            </label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem
                              value="substitute"
                              id="teaching-pref-substitute"
                              data-testid="my-schedule-weekly-hours-radio-substitute"
                            />
                            <label htmlFor="teaching-pref-substitute" className="text-sm">
                              Substitute teaching only
                            </label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem
                              value="both"
                              id="teaching-pref-both"
                              data-testid="my-schedule-weekly-hours-radio-both"
                            />
                            <label htmlFor="teaching-pref-both" className="text-sm">
                              Both Regular and Substitute
                            </label>
                          </div>
                        </RadioGroup>
                        <div className="bg-[#F3F4F6] rounded-md text-[#64748B] p-2 my-4">
                          <div className="font-bold text-base leading-6 tracking-tight align-middle">
                            Regular Schedule
                          </div>
                          <div className="font-normal text-base leading-6 tracking-tight align-middle">
                            Set your weekly availability for new student assignments and diagnostic assessments. Please
                            only select time slots where you are consistently available for the next 2–3 months. <br />
                            &nbsp;&nbsp;• Requirements: Classes are 30–40 minutes long, occur at the same time each day,
                            and require at least 3 days of availability per week. <br />
                            &nbsp;&nbsp;• Existing Groups: Updating your schedule will not affect your current classes.
                            To request a change for an existing group,
                            <a
                              href="https://help.airreading.com/support/tickets/new"
                              target="_blank"
                              className="text-[#2563EB]"
                            >
                              {' '}
                              please submit a support ticket
                            </a>
                            .
                          </div>
                        </div>
                        <div className="bg-[#F3F4F6] rounded-md mt-2 text-[#64748B] p-2">
                          <div className="font-bold text-base leading-6 tracking-tight align-middle">
                            Substitute Teaching
                          </div>
                          <div className="font-normal text-base leading-6 tracking-tight align-middle">
                            By opting into substitute classes, you may be assigned sessions up to 12 hours before the
                            start time. You will receive an email notification as soon as you are assigned a sub shift.
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="font-normal text-base leading-6 tracking-tight align-middle ml-2 mt-6">
                        {profile.teachingPreferences.includes(TeachingPreference.regular_schedule) &&
                        profile.teachingPreferences.includes(TeachingPreference.substitute_teaching)
                          ? 'Both Regular Schedule and Substitute Teaching'
                          : profile.teachingPreferences.map(getTeachingPreferenceLabel)}
                      </div>
                    )}
                  </>
                )}
                <div className="grid grid-cols-1 gap-3 pt-6">
                  {SortedDaysOfWeek.map((day, dayId) => {
                    return (
                      <WeeklyHoursFormRow
                        key={day}
                        day={day}
                        dayId={dayId}
                        form={form}
                        editing={editing}
                        timeZoneAbbr={standardAbbr}
                        onPastMidnightClick={handlePastMidnightClick}
                      />
                    );
                  })}
                </div>
              </>
            );
          }}
        </EditableInfoPage>
      </Card>
      <Dialog open={!!pastMidnightSlot} onOpenChange={(open) => !open && setPastMidnightSlot(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-xl text-center">How to open a time slot that crosses midnight</DialogTitle>
          </DialogHeader>
          <DialogDescription className="text-[#000000]">
            Time slots cannot extend across two calendar days, so they MUST be opened separately for each day. <br />
            For example, Monday 11:30 pm – 2:00 am, please split it into two separate slots:
          </DialogDescription>
          <img src={ScheduleMidnightImage} alt="Schedule Midnight" />
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="destructive">
                OK
              </Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
